import { consumeDependency } from '@jira/router-resources__utils/src/consumeDependency.tsx';
import { createResourceError } from '@jira/router-resources__utils/src/createResourceError.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	createResource,
	useResource,
	type RouteResource,
	type RouterDataContext,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';

import { fetchAvpDashboard, type AvpDashboard } from './fetchAvpDashboard.tsx';

const RESOURCE_TYPE_AVP_DASHBOARD_CONFIG = 'AVP_DASHBOARD_CONFIG' as const;

const getDashboardId = async ({
	match,
	dependencies,
}: RouterDataContext): Promise<string | null> => {
	const [dependencyKey] = Object.keys(dependencies);
	const paramId = match.params.dashboardId;
	if (paramId) {
		return paramId;
	}
	if (!dependencyKey) {
		return null;
	}
	const dependency = await consumeDependency<{ dashboardId: string | null } | null>(
		dependencies,
		dependencyKey,
	);
	return dependency?.dashboardId ?? null;
};

export const dashboardConfigResource: RouteResource<AvpDashboard | null> = createResource({
	type: RESOURCE_TYPE_AVP_DASHBOARD_CONFIG,
	getKey: ({ match: { params } }, { tenantContext: { activationId, cloudId } }) =>
		// dashboardId is a dependent resource and we don't want to have to know its key
		// so unfortunately we need to just take all the params and dilute the resource cache
		JSON.stringify({ ...params, activationId, cloudId }),
	isBrowserOnly: true,
	getData: async (routerContext, { tenantContext: { activationId, cloudId } }) => {
		const __startTime = fg('platform_avp_custom-fetch-spans_dashboard') ? performance.now() : null;
		try {
			if (!activationId) {
				throw new Error('Route config is missing activation ID');
			}
			if (!cloudId) {
				throw new Error('Route config is missing cloud ID');
			}
			const dashboardId = await getDashboardId(routerContext);
			if (!dashboardId) {
				// legal edge case given the dashboard is not created until first visit
				return null;
			}
			const data = await fetchAvpDashboard(activationId, cloudId, dashboardId);
			return __startTime && data
				? { ...data, __metadata: { startTime: __startTime, endTime: performance.now() } }
				: data;
		} catch (error) {
			throw createResourceError(
				{
					component: 'spa.view.routes.viz-platform.getData',
					message: 'Error fetching dashboard',
					name: 'ResourceError',
				},
				error,
			);
		}
	},
	maxAge: 60_000,
});

export const useDashboardConfig = (): UseResourceHookResponse<AvpDashboard | null> =>
	useResource(dashboardConfigResource);
